// Tipos preview do contrato de Workflow (M1 §5/§6 do plano de domínio) — TODO(GIY-023): substituir por
// `components['schemas'][…]` de `@giyus/api-client` quando `contracts/openapi/v1.json` trouxer estas rotas
// (integração após A/B mesclarem). Nomes de tipo espelham exatamente os nomes de schema previstos no plano.
import type { SignatureMeaning } from '../../../components/signatureMeanings';

export type WorkItemKind = 'task' | 'approval' | 'exception';
export type ScopeLevel = 'Network' | 'Tenant' | 'Study';
export type OwnerKind = 'User' | 'Role';

export interface WorkflowOwnerRef {
  readonly kind: OwnerKind;
  readonly userId?: string | null;
  readonly roleCode?: string | null;
}

export interface WorkflowScopeRef {
  readonly level: ScopeLevel;
  readonly tenantId?: string | null;
  readonly studySiteId?: string | null;
}

export interface WorkflowSourceRef {
  readonly entityType: string;
  readonly entityId: string;
  readonly label?: string | null;
}

export interface WorkflowConsequence {
  readonly messageKey?: string | null;
  readonly args?: Readonly<Record<string, string>> | null;
  readonly detail?: string | null;
}

export interface WorkflowApprovalSummary {
  readonly actType: string;
  readonly objectType: string;
  readonly objectId: string;
  readonly objectVersion?: string | null;
  readonly objectSummary?: string | null;
  readonly objectContentHash: string;
  readonly step: number;
  readonly requiresSignature: boolean;
  readonly allowedMeanings: readonly SignatureMeaning[];
}

export type TaskStatus = 'Open' | 'InProgress' | 'Blocked' | 'Done' | 'Cancelled';
export type ApprovalStatus =
  'Pending' | 'PendingSecondKey' | 'Approved' | 'Rejected' | 'Returned' | 'Expired' | 'Cancelled';
export type ExceptionStatus = 'Open' | 'Assigned' | 'Resolved' | 'Dismissed';
export type WorkPriority = 'Low' | 'Normal' | 'High' | 'Urgent';
export type ExceptionSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

/** `WorkflowInboxItemResponse` (namespace `…Endpoints.Inbox`, tipo `ItemResponse`). */
export interface WorkflowInboxItem {
  readonly kind: WorkItemKind;
  readonly id: string;
  readonly typeCode: string;
  readonly subject?: string | null;
  readonly status: TaskStatus | ApprovalStatus | ExceptionStatus;
  readonly priority?: WorkPriority | null;
  readonly severity?: ExceptionSeverity | null;
  readonly dueAt?: string | null;
  readonly overdue: boolean;
  readonly createdAt: string;
  readonly scope: WorkflowScopeRef;
  readonly owner: WorkflowOwnerRef;
  readonly assignedToMe: boolean;
  readonly claimable: boolean;
  readonly source?: WorkflowSourceRef | null;
  readonly consequence?: WorkflowConsequence | null;
  readonly approval?: WorkflowApprovalSummary | null;
  readonly allowedActions: readonly string[];
}

export interface WorkflowInboxItemsResponse {
  readonly items: readonly WorkflowInboxItem[];
  readonly nextCursor: string | null;
}

export interface WorkflowInboxCountersResponse {
  readonly total: number;
  readonly byKind: { readonly task: number; readonly approval: number; readonly exception: number };
  readonly overdue: number;
  readonly byScope: readonly { readonly tenantId: string | null; readonly count: number }[];
}

export interface WorkflowChecklistItem {
  readonly id: string;
  readonly order: number;
  readonly key?: string | null;
  readonly label?: string | null;
  readonly isRequired: boolean;
  readonly isChecked: boolean;
  readonly checkedAt?: string | null;
  readonly checkedBy?: string | null;
}

export interface WorkflowComment {
  readonly id: string;
  readonly body: string;
  readonly authorId: string;
  readonly authorName: string;
  readonly createdAt: string;
  readonly deletedAt?: string | null;
  readonly deleteReason?: string | null;
}

export interface WorkflowHistoryEntry {
  readonly id: string;
  readonly action: string;
  readonly actorName: string;
  readonly actorRole: string;
  readonly occurredAt: string;
  readonly reason?: string | null;
  readonly summary?: string | null;
}

export interface WorkflowTaskDetail extends WorkflowInboxItem {
  readonly kind: 'task';
  readonly description?: string | null;
  readonly blockedReason?: string | null;
  readonly assigneeUserId?: string | null;
  readonly assigneeName?: string | null;
  readonly requiresEvidence: boolean;
  readonly evidencePresent: boolean;
  readonly checklist: readonly WorkflowChecklistItem[];
  readonly comments: readonly WorkflowComment[];
  readonly history: readonly WorkflowHistoryEntry[];
  readonly etag: string;
}

export interface WorkflowApprovalDetail extends WorkflowInboxItem {
  readonly kind: 'approval';
  readonly approval: WorkflowApprovalSummary;
  readonly decisions: readonly {
    readonly id: string;
    readonly step: number;
    readonly deciderName: string;
    readonly decision: 'Approved' | 'Rejected' | 'Returned';
    readonly meaning: SignatureMeaning;
    readonly comment?: string | null;
    readonly decidedAt: string;
  }[];
  readonly etag: string;
}

export interface WorkflowExceptionDetail extends WorkflowInboxItem {
  readonly kind: 'exception';
  readonly detail?: string | null;
  readonly occurrenceCount: number;
  readonly lastOccurredAt: string;
  readonly slaDueAt: string;
  readonly history: readonly WorkflowHistoryEntry[];
  readonly etag: string;
}

export type WorkflowItemDetail =
  WorkflowTaskDetail | WorkflowApprovalDetail | WorkflowExceptionDetail;

export interface WorkflowMetricsRow {
  readonly kind: WorkItemKind;
  readonly typeCode: string;
  readonly open: number;
  readonly overdue: number;
  readonly aging: {
    readonly lt1d: number;
    readonly d1to3: number;
    readonly d3to7: number;
    readonly gt7d: number;
  };
  readonly resolvedInPeriod: number;
  readonly meanResolutionHours?: number | null;
}

export interface WorkflowApprovalPolicyStep {
  readonly order: number;
  readonly approverRoleCodes: readonly string[];
  readonly approverScope: 'SameScope' | 'Network';
  readonly requiredPermission: string;
  readonly distinctFromPreviousSteps: boolean;
}

export interface WorkflowApprovalPolicy {
  readonly actType: string;
  readonly ownerModule: string;
  readonly steps: readonly WorkflowApprovalPolicyStep[];
  readonly dueDays?: number | null;
  readonly requiresSignature: boolean;
  readonly allowedMeanings: readonly SignatureMeaning[];
  readonly requiresTwoKeys: boolean;
  readonly status: 'Active' | 'Inactive';
}

export interface WorkflowEscalationRule {
  readonly code: string;
  readonly targetKind: 'Task' | 'Approval' | 'Exception';
  readonly typeCode?: string | null;
  readonly minUrgency?: string | null;
  readonly level: number;
  readonly basis: 'SinceLastActivity' | 'SinceDue';
  readonly afterHours: number;
  readonly action: 'Reassign' | 'FollowUpTask';
  readonly targetRoleCode: string;
  readonly targetScope: 'SameTenant' | 'Network';
  readonly status: 'Active' | 'Inactive';
}

export interface WorkflowTasksResponse {
  readonly items: readonly WorkflowTaskDetail[];
}
export interface WorkflowApprovalsResponse {
  readonly items: readonly WorkflowApprovalDetail[];
}
export interface WorkflowExceptionsResponse {
  readonly items: readonly WorkflowExceptionDetail[];
}

export interface InboxItemsQuery {
  readonly kind?: readonly WorkItemKind[];
  readonly tenantId?: string | null;
  readonly network?: boolean;
  readonly typeCode?: string | null;
  readonly overdue?: boolean;
  readonly cursor?: string | null;
  readonly limit?: number;
}

/** Rotas do plano §6.2 (preview). */
export const WORKFLOW_PREVIEW_PATHS = {
  meInbox: '/api/v1/me/inbox',
  meInboxCounters: '/api/v1/me/inbox/counters',
  meInboxItem: (kind: WorkItemKind, id: string) => `/api/v1/me/inbox/items/${kind}/${id}`,
  tasks: (scope: string) => `/api/v1/${scope}/tasks`,
  task: (scope: string, id: string) => `/api/v1/${scope}/tasks/${id}`,
  taskAction: (scope: string, id: string, action: string) =>
    `/api/v1/${scope}/tasks/${id}/${action}`,
  taskChecklistItem: (scope: string, id: string, itemId: string) =>
    `/api/v1/${scope}/tasks/${id}/checklist/${itemId}`,
  taskComments: (scope: string, id: string) => `/api/v1/${scope}/tasks/${id}/comments`,
  approvals: (scope: string) => `/api/v1/${scope}/approvals`,
  approval: (scope: string, id: string) => `/api/v1/${scope}/approvals/${id}`,
  approvalDecide: (scope: string, id: string) => `/api/v1/${scope}/approvals/${id}/decide`,
  exceptions: (scope: string) => `/api/v1/${scope}/exceptions`,
  exception: (scope: string, id: string) => `/api/v1/${scope}/exceptions/${id}`,
  exceptionAction: (scope: string, id: string, action: string) =>
    `/api/v1/${scope}/exceptions/${id}/${action}`,
  metrics: (scope: string) => `/api/v1/${scope}/workflow/metrics`,
  approvalPolicies: '/api/v1/network/workflow/approval-policies',
  escalationRules: '/api/v1/network/workflow/escalation-rules',
} as const;
